import { Html, OrbitControls, useGLTF } from "@react-three/drei";

import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import * as THREE from "three";
import gsap from "gsap";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";
import { disableNormalMipmapsOnScene } from "./Utils/RenderRig";
import { applyMaterialOverrides } from "./Utils/MaterialOverrides";
import SeatPracticalLights from "./Utils/SeatPracticalLights";
import D19P4C_PLights from "./lights/D19P4C_PLights.json";
import D19P55C_PLights from "./lights/D19P55C_PLights.json";
import D19P65C_PLights from "./lights/D19P65C_PLights.json";
import D19P85C_PLights from "./lights/D19P85C_PLights.json";
import D21P85C_PLights from "./lights/D21P85C_PLights.json";
import Q19P4C_PLights from "./lights/Q19P4C_PLights.json";
import T19P85C_PLights from "./lights/T19P85C_PLights.json";

import {
  inch4,
  inch5_5,
  inch6_5,
  inch8_5,
  quadPax,
  triplePax,
} from "../../util/MaterialsList";
import { glbVersion } from "../../variables/glbVersion";
import { SceneEnvPath } from "../../variables/uris";

import {
  RenderStack,
  QUALITY_PRESETS,
  ADAPTIVE_QUALITY_CONFIG,
  clampPresetId,
} from "./Utils/RenderRig";
import { lights } from "three/src/nodes/TSL.js";
import { getSeatCode, getSeatDetails } from "../../util/getSeatCode";

function SharedLoaders({ children }) {
  const { gl } = useThree();

  const ktx2Loader = useMemo(() => {
    const loader = new KTX2Loader().setTranscoderPath("/basis/");
    loader.detectSupport(gl);
    return loader;
  }, [gl]);

  useEffect(() => {
    return () => {
      ktx2Loader.dispose();
    };
  }, [ktx2Loader]);

  return children(ktx2Loader);
}

function setMeshOpacity(child, opacity) {
  const materials = Array.isArray(child.material)
    ? child.material
    : [child.material];

  materials.forEach((material) => {
    if (!material) return;

    material.transparent = opacity < 1;
    material.opacity = opacity;
    material.depthWrite = opacity >= 1;
    material.needsUpdate = true;
  });
}

function animateMeshOpacity(child, opacity, invalidate, duration = 0.2) {
  const materials = Array.isArray(child.material)
    ? child.material
    : [child.material];

  materials.forEach((material) => {
    if (!material) return;

    gsap.killTweensOf(material);
    material.transparent = true;
    material.depthWrite = false;
    material.needsUpdate = true;

    gsap.to(material, {
      opacity,
      duration,
      ease: "power1.out",
      onUpdate: () => {
        material.needsUpdate = true;
        invalidate();
      },
      onComplete: () => {
        material.transparent = opacity < 1;
        material.depthWrite = opacity >= 1;
        material.needsUpdate = true;
        invalidate();
      },
    });
  });
}

function Model({
  url,
  data = [],
  dataSelection = {},
  onMaterialClick,
  groupUrlKey,
  setGlbsLoaded,
  renderEnabled = true,
  ktx2Loader,
  modelRef,
  isNight,
  isFoodTrayVisible = true,
  seatCode = "",
}) {
  let groupRef = useRef();
  const { invalidate } = useThree();
  const previousFoodTrayVisible = useRef(isFoodTrayVisible);
  if (modelRef) groupRef = modelRef;
  const { scene: cachedGlbScene } = useGLTF(url, false, false, (loader) => {
    if (ktx2Loader) loader.setKTX2Loader(ktx2Loader);
  });
  const glbScene = useMemo(() => cachedGlbScene?.clone(true), [cachedGlbScene]);

  const originalColors = useRef(new Map());
  const [
    subStepTitle,
    context1,
    cascadeName1,
    context2,
    cascadeName2,
    context3,
    cascadeName3,
    context4,
    cascadeName4,
  ] = groupUrlKey.split("|");
  const selectedVariantId =
    dataSelection?.[subStepTitle]?.[cascadeName1]?.[context1];
  const selectedVariantId2 =
    dataSelection?.[subStepTitle]?.[cascadeName2]?.[context2];
  const selectedVariantId3 =
    dataSelection?.[subStepTitle]?.[cascadeName3]?.[context3];
  const selectedVariantId4 =
    dataSelection?.[subStepTitle]?.[cascadeName4]?.[context4];
  const seatTypeSelection =
    dataSelection?.["Product Installation"]?.["Seat Type"]?.["Seat Type"];
  const consoleWidthSelection =
    dataSelection?.["Product Installation"]?.["Console Width"]?.[
      "Console Width"
    ];
  const backStitchSelection =
    dataSelection?.["BACK COVER"]?.["Back Decorative Stitch"]?.[
      "Back Decorative Stitch"
    ];
  const bottomStitchSelection =
    dataSelection?.["BOTTOM COVER"]?.["Bottom Decorative Stitch"]?.[
      "Bottom Decorative Stitch"
    ];
  const headrestStitchSelection =
    dataSelection?.["HEADREST COVER"]?.["Headrest Decorative Stitch"]?.[
      "Headrest Decorative Stitch"
    ];
  const backCoverMaterialSelections = getCoverMaterialSelections(
    dataSelection,
    "BACK COVER",
    "Back",
  );
  const backCoverSecondaryMaterialSelections = getCoverMaterialSelections(
    dataSelection,
    "BACK COVER",
    "Back",
    true,
  );
  const bottomCoverMaterialSelections = getCoverMaterialSelections(
    dataSelection,
    "BOTTOM COVER",
    "Bottom",
  );
  const bottomCoverSecondaryMaterialSelections = getCoverMaterialSelections(
    dataSelection,
    "BOTTOM COVER",
    "Bottom",
    true,
  );
  const headrestCoverMaterialSelections = getCoverMaterialSelections(
    dataSelection,
    "HEADREST COVER",
    "Headrest",
  );
  const headrestCoverSecondaryMaterialSelections = getCoverMaterialSelections(
    dataSelection,
    "HEADREST COVER",
    "Headrest",
    true,
  );
  const isFoodTrayModel =
    // groupUrlKey.startsWith("FOOD TRAY") ||
    url.includes("/43_Food_Tray/") || url.includes("/43_PED_Holder/");

  useEffect(() => {
    if (!glbScene) return;
    let count = 0;
    glbScene.traverse((o) => {
      if (o.isMesh) {
        const m = o.material;
        if (m?.map) count++;
      }
    });
  }, [glbScene]);

  useEffect(() => {
    if (!glbScene) return;
    if (data) {
      const visibleModel = data.find(
        (option) => option.VariantID === selectedVariantId?.split("_#")[0],
      );
      const visibleModel2 = data.find(
        (option) => option.VariantID === selectedVariantId2?.split("_#")[0],
      );
      const visibleModel3 = data.find(
        (option) => option.VariantID === selectedVariantId3?.split("_#")[0],
      );
      const visibleModel4 = data.find(
        (option) => option.VariantID === selectedVariantId4?.split("_#")[0],
      );

      const selectedModels = [
        visibleModel,
        visibleModel2,
        visibleModel3,
        visibleModel4,
      ].filter(Boolean);

      const resolveOptionUrls = (option) => {
        let optionUrl = option?.Url;

        // Add the "None" variants to the URL string if applicable
        if (
          optionUrl &&
          (optionUrl.indexOf("backDressCover") > -1 ||
            optionUrl.indexOf("bottomDress") > -1 ||
            optionUrl.indexOf("headrest") > -1)
        ) {
          optionUrl = optionUrl + "|" + replaceUrlWithStitchVar(optionUrl);
        }

        return String(optionUrl || "")
          .split("|")
          .filter(Boolean)
          .map((u) => getActualUrl(seatCode, u));
      };

      const selectedUrlLists = selectedModels
        .map(resolveOptionUrls)
        .filter((urls) => urls.length > 0);

      let resolvedVisibleUrls = [];
      if (selectedUrlLists.length > 0) {
        resolvedVisibleUrls = selectedUrlLists.reduce((commonUrls, urls) =>
          commonUrls.filter((commonUrl) => urls.includes(commonUrl)),
        );
      }

      let visibleUrl = resolvedVisibleUrls.join("|");

      // 3. PRE-FILTER: Determine user stitch selection before the greedy assignment
      let userStitchSelection = "collins";
      const backDS = "Back Decorative Stitch";
      const bottomDS = "Bottom Decorative Stitch";
      const headrestDS = "Headrest Decorative Stitch";

      if (groupUrlKey.includes("BACK COVER")) {
        userStitchSelection = dataSelection["BACK COVER"]?.[backDS]?.[backDS];
      } else if (groupUrlKey.includes("BOTTOM COVER")) {
        userStitchSelection =
          dataSelection["BOTTOM COVER"]?.[bottomDS]?.[bottomDS];
      } else if (groupUrlKey.includes("HEADREST COVER")) {
        userStitchSelection =
          dataSelection["HEADREST COVER"]?.[headrestDS]?.[headrestDS];
      }

      const isFunctionalSeamModel = groupUrlKey.includes("SEAM STYLE");
      const wantsNone = userStitchSelection?.toLowerCase().includes("none");

      // Filter visibleUrl to only include paths matching the stitch selection
      // This prevents the greedy assignment from selecting the wrong stitch GLB
      let filteredVisibleUrl = visibleUrl
        .split("|")
        .filter((path) => {
          if (!path) return false;
          if (isFunctionalSeamModel) return true;
          if (wantsNone) return !path.includes("_DS"); // Remove DS if user wants None
          return !path.includes("_Cover"); // Remove None if user wants Collins
        })
        .join("|");

      // 4. GREEDY ASSIGNMENT (Now using the filtered list)
      if (filteredVisibleUrl?.indexOf(url) > -1) {
        visibleUrl = url;
      } else {
        visibleUrl = filteredVisibleUrl;
      }

      if (!visibleModel && !visibleModel2 && !visibleModel3 && !visibleModel4)
        visibleUrl = url;

      applyMaterialOverrides(glbScene, { renderEnabled });

      glbScene.traverse((child) => {
        if (child.isMesh) {
          child.layers.set(0);
          child.userData.dofTarget = true;
          child.castShadow = !!renderEnabled;
          child.receiveShadow = !!renderEnabled;

          if (child.material) {
            if (renderEnabled) {
              child.material.dithering = true;
              if ("envMapIntensity" in child.material) {
                child.material.envMapIntensity = 1.5;
              }
            }
            child.material.needsUpdate = false;
          }
        }

        if (child.isMesh && child.material?.color) {
          if (getMaterialList(dataSelection).includes(child.material.name)) {
            originalColors.current.set(
              child.material.name,
              child.material.color.clone(),
            );
          }
        }

        if (
          selectedVariantId?.indexOf("color") > -1 ||
          selectedVariantId2?.indexOf("color") > -1 ||
          selectedVariantId3?.indexOf("color") > -1 ||
          selectedVariantId4?.indexOf("color") > -1
        ) {
          // console.log(
          //   visibleUrl,
          //   selectedVariantId,
          //   selectedVariantId2,
          //   selectedVariantId3,
          //   selectedVariantId4,
          //   "-----",
          // );
          let color =
            selectedVariantId?.split("_#")[1] ||
            selectedVariantId2?.split("_#")[1] ||
            selectedVariantId3?.split("_#")[1] ||
            selectedVariantId4?.split("_#")[1];
          if (
            selectedVariantId3?.split("_#")[1] &&
            selectedVariantId3.indexOf("dual") > -1 &&
            selectedVariantId3.indexOf("primary") > -1 &&
            url.indexOf("Mono") > -1
          ) {
            color = selectedVariantId3?.split("_#")[1];
          }

          if (
            selectedVariantId4?.split("_#")[1] &&
            selectedVariantId4.indexOf("dual") > -1 &&
            selectedVariantId4.indexOf("secondary") > -1 &&
            url.indexOf("Dual") > -1
          ) {
            color = selectedVariantId4?.split("_#")[1];
          }

          // selectedVariantId3?.split("_#")[1]
          // selectedVariantId4?.split("_#")[1]
          if (color && child?.material?.color)
            child.material.color.set("#" + color);
        }

        // Final Visibility Check
        // stitchMatch is no longer needed here because we pre-filtered visibleUrl
        child.visible =
          (visibleUrl === url || data.length === 0) &&
          selectedVariantId !== "-";

        function isMatMatch(matSelectionID, urlStr, urlPrefix, material) {
          if (!matSelectionID || !urlStr) return false;
          matSelectionID = String(matSelectionID).toLowerCase();
          urlStr = String(urlStr).toLowerCase();
          return (
            matSelectionID.indexOf(material) > -1 &&
            urlStr.indexOf(urlPrefix + material) > -1
          );
        }

        if (
          child.visible &&
          !isFunctionalSeamModel &&
          (url.indexOf("_MonoTone_") > -1 ||
            url.indexOf("_DualTone_") > -1 ||
            url.indexOf("_Mono_Tone_") > -1 ||
            url.indexOf("_Dual_Tone_") > -1 ||
            url.indexOf("141_Bottom_Stitch_None") > -1) &&
          url.indexOf("default") <= -1
        ) {
          const coverMaterialSelections = getCoverMaterialSelectionsForModel(
            dataSelection,
            groupUrlKey,
            url,
          );
          let fabricSelID = coverMaterialSelections?.fabric;
          let muLeatSelID = coverMaterialSelections?.muirhead;
          let perLeathSelID = coverMaterialSelections?.perrone;
          let smLeathSelID = coverMaterialSelections?.fauxLeather;

          if (!coverMaterialSelections) return;

          child.visible = false;

          if (isMatMatch(fabricSelID, url, "~", "rohi")) child.visible = true;
          if (isMatMatch(fabricSelID, url, "~", "bot")) child.visible = true;
          if (isMatMatch(muLeatSelID, url, "-", "df")) child.visible = true;
          if (isMatMatch(muLeatSelID, url, "-", "dl")) child.visible = true;
          if (isMatMatch(perLeathSelID, url, "-", "sky")) child.visible = true;
          if (isMatMatch(perLeathSelID, url, "-", "eli")) child.visible = true;
          if (isMatMatch(perLeathSelID, url, "-", "feath"))
            child.visible = true;
          if (isMatMatch(smLeathSelID, url, "~", "tapi")) child.visible = true;
        }
      });

      if (isFoodTrayModel) {
        const shouldAnimateFoodTray =
          previousFoodTrayVisible.current !== isFoodTrayVisible;

        glbScene.traverse((child) => {
          if (child.isMesh) {
            const targetOpacity = isFoodTrayVisible ? 1 : 0;

            if (shouldAnimateFoodTray) {
              animateMeshOpacity(child, targetOpacity, invalidate);
            } else {
              setMeshOpacity(child, targetOpacity);
            }
          }
        });

        previousFoodTrayVisible.current = isFoodTrayVisible;
      }

      disableNormalMipmapsOnScene(glbScene);

      if (groupRef.current && glbScene.parent !== groupRef.current) {
        groupRef.current.add(glbScene);
      }
      setGlbsLoaded?.(true);
      invalidate();
    }
  }, [
    selectedVariantId,
    selectedVariantId2,
    selectedVariantId3,
    selectedVariantId4,
    seatTypeSelection,
    consoleWidthSelection,
    backStitchSelection,
    bottomStitchSelection,
    headrestStitchSelection,
    backCoverMaterialSelections.fabric,
    backCoverMaterialSelections.muirhead,
    backCoverMaterialSelections.perrone,
    backCoverMaterialSelections.fauxLeather,
    backCoverSecondaryMaterialSelections.fabric,
    backCoverSecondaryMaterialSelections.muirhead,
    backCoverSecondaryMaterialSelections.perrone,
    backCoverSecondaryMaterialSelections.fauxLeather,
    bottomCoverMaterialSelections.fabric,
    bottomCoverMaterialSelections.muirhead,
    bottomCoverMaterialSelections.perrone,
    bottomCoverMaterialSelections.fauxLeather,
    bottomCoverSecondaryMaterialSelections.fabric,
    bottomCoverSecondaryMaterialSelections.muirhead,
    bottomCoverSecondaryMaterialSelections.perrone,
    bottomCoverSecondaryMaterialSelections.fauxLeather,
    headrestCoverMaterialSelections.fabric,
    headrestCoverMaterialSelections.muirhead,
    headrestCoverMaterialSelections.perrone,
    headrestCoverMaterialSelections.fauxLeather,
    headrestCoverSecondaryMaterialSelections.fabric,
    headrestCoverSecondaryMaterialSelections.muirhead,
    headrestCoverSecondaryMaterialSelections.perrone,
    headrestCoverSecondaryMaterialSelections.fauxLeather,
    glbScene,
    url,
    data,
    groupUrlKey,
    renderEnabled,
    isFoodTrayModel,
    isFoodTrayVisible,
    invalidate,
  ]);

  return !glbScene ? null : <group ref={groupRef} />;
}

function CaptureHelper({ onCaptureReady }) {
  const { gl, scene } = useThree();
  const captureCamera = useRef();

  useEffect(() => {
    // Create secondary camera
    captureCamera.current = new THREE.PerspectiveCamera(24, 1, 0.1, 2000);

    // Expose capture function
    const captureFn = () => {
      const prevSize = gl.getSize(new THREE.Vector2());
      const targetWidth = 550;
      const targetHeight = 550;

      // Function to capture one image given a camera position
      const captureAt = (pos, zoom = 1) => {
        captureCamera.current.position.copy(pos.clone().multiplyScalar(zoom));
        captureCamera.current.lookAt(0, 0, 0);

        gl.setSize(targetWidth, targetHeight);
        gl.render(scene, captureCamera.current);

        return gl.domElement.toDataURL("image/png");
      };

      // Define camera base positions
      const frontPos = new THREE.Vector3(-150, 10, 150);
      const backPos = new THREE.Vector3(150, 10, 150);

      // Capture 4 images
      const images = [
        captureAt(frontPos, 1),
        captureAt(frontPos, 0.7),
        captureAt(backPos, 1),
        captureAt(backPos, 0.7),
      ];

      // Reset renderer size
      gl.setSize(prevSize.x, prevSize.y);

      return images;
    };

    onCaptureReady(captureFn);
  }, [gl, scene, onCaptureReady]);

  return null;
}

function DemandOrbitControls({ controlsRef, sceneRootRef }) {
  const { gl, invalidate } = useThree();

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (event.button !== 0 || event.ctrlKey || !sceneRootRef?.current) {
        return;
      }

      gsap.killTweensOf(sceneRootRef.current.position);
      sceneRootRef.current.position.x = 0;
      sceneRootRef.current.position.y = 0;
      invalidate();
    };

    const canvas = gl.domElement;
    canvas.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      canvas.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [gl, invalidate, sceneRootRef]);

  return (
    <OrbitControls
      ref={controlsRef}
      makeDefault
      enableZoom={true}
      enableDamping={false}
      onChange={invalidate}
    />
  );
}

/* =============================================================================
   4) DATA HELPERS (UNCHANGED)
============================================================================= */

function getActualUrl(seatCode, url) {
  url =
    "/Assets/Phoenix_2_0/all_V" +
    glbVersion +
    "_seats/[SEATCODE]_V00" +
    glbVersion +
    url;

  if (url) return url.replaceAll("[SEATCODE]", seatCode);
  else return url;
}

function getLightsBasedOnSeat(seatCode) {
  if (seatCode === "D19P4C") {
    return D19P4C_PLights;
  }
  if (seatCode === "D19P55C") {
    return D19P55C_PLights;
  }
  if (seatCode === "D19P65C") {
    return D19P65C_PLights;
  }
  if (seatCode === "D19P85C") {
    return D19P85C_PLights;
  }
  if (seatCode === "Q19P4C") {
    return Q19P4C_PLights;
  }
  if (seatCode === "T19P85C") {
    return T19P85C_PLights;
  }
}

function getMaterialList(dataSelection) {
  let consoleWidth =
    dataSelection["Product Installation"]["Console Width"][
      "Console Width"
    ].split("_")[3];

  let seatType =
    dataSelection["Product Installation"]["Seat Type"]["Seat Type"];

  if (seatType.indexOf("widebody_pax_number_quad") > -1) return quadPax;
  else if (seatType.indexOf("widebody_pax_number_triple") > -1)
    return triplePax;
  else if (consoleWidth === "4") {
    return inch4;
  } else if (consoleWidth === "5.5") {
    return inch5_5;
  } else if (consoleWidth === "6.5") {
    return inch6_5;
  } else if (consoleWidth === "8.5") {
    return inch8_5;
  } else return [];
}

function getCoverMaterialSelections(
  dataSelection,
  subStepTitle,
  contextPrefix,
  secondary = false,
) {
  const materialPrefix = secondary
    ? `${contextPrefix} Secondary`
    : contextPrefix;
  const surfaceMaterial =
    dataSelection?.[subStepTitle]?.[`${materialPrefix} Surface Material`];

  return {
    fabric: surfaceMaterial?.[`${materialPrefix} Fabric Cover`],
    muirhead: surfaceMaterial?.[`${materialPrefix} Muirhead Cover`],
    perrone: surfaceMaterial?.[`${materialPrefix} Perrone Cover`],
    fauxLeather: surfaceMaterial?.[`${materialPrefix} Faux Leather Cover`],
  };
}

function getCoverMaterialSelectionsForModel(dataSelection, groupUrlKey, url) {
  const key = `${groupUrlKey || ""} ${url || ""}`;
  const isSecondaryMaterial = key.includes("blockS.glb");

  if (
    key.includes("BACK COVER") ||
    key.includes("BackCover") ||
    key.includes("Back_Dress_Cover")
  ) {
    return getCoverMaterialSelections(
      dataSelection,
      "BACK COVER",
      "Back",
      isSecondaryMaterial,
    );
  }

  if (
    key.includes("BOTTOM COVER") ||
    key.includes("BottomCover") ||
    key.includes("Bottom_Cover") ||
    key.includes("Bottom_Stitch_None")
  ) {
    return getCoverMaterialSelections(
      dataSelection,
      "BOTTOM COVER",
      "Bottom",
      isSecondaryMaterial,
    );
  }

  if (
    key.includes("HEADREST COVER") ||
    key.includes("Headrest") ||
    key.includes("Head_Rest_Cover")
  ) {
    return getCoverMaterialSelections(
      dataSelection,
      "HEADREST COVER",
      "Headrest",
      isSecondaryMaterial,
    );
  }

  return null;
}

/* =============================================================================
   HUD (FOR DEBUGGING PURPOSES)
============================================================================= */

/* =============================================================================
   5) MAIN VIEWER (QUALITY WIRED IN, FEATURES UNCHANGED)
============================================================================= */

function replaceUrlWithStitchVar(path) {
  let path_ = path;
  if (!path_ || path_.indexOf("~stitchingThread~__default") > -1) return path_;

  // Handle MonoTone
  if (path_.indexOf("405_MonoTone_BackCover_DS") > -1) {
    path_ = path_
      .replaceAll("405_MonoTone_BackCover_DS", "72_Mono_Tone_Back_Dress_Cover")
      .replaceAll("DS_", "_")
      .replaceAll("block5", "block4");
  }

  // Handle DualTone (Now in its own check so it's actually reached)
  if (path_.indexOf("401_DualTone_BackCover_DS") > -1) {
    path_ = path_
      .replaceAll("401_DualTone_BackCover_DS", "73_Dual_Tone_Back_Dress_Cover")
      .replaceAll("DS_", "_")
      .replaceAll("block5", "block4");
  }

  if (path_.indexOf("408_MonoTone_BottomCover_DS") > -1) {
    path_ = path_
      .replaceAll("408_MonoTone_BottomCover_DS", "141_Bottom_Stitch_None")
      .replaceAll("DS_", "_")
      .replaceAll("block7", "block6");
  }

  if (path_.indexOf("400_DualTone_BottomCover_DS") > -1) {
    path_ = path_
      .replaceAll("400_DualTone_BottomCover_DS", "147_Dual_Tone_Bottom_Cover")
      .replaceAll("DS_", "_")
      .replaceAll("block8", "block6");
  }

  if (path_.indexOf("410_MonoTone_Headrest_DS") > -1) {
    path_ = path_
      .replaceAll("410_MonoTone_Headrest_DS", "93_Mono_Tone_Head_Rest_Cover")
      .replaceAll("DS_", "_")
      .replaceAll("block10", "block9");
  }

  if (path_.indexOf("411_DualTone_Headrest_DS") > -1) {
    path_ = path_
      .replaceAll("411_DualTone_Headrest_DS", "94_Dual_Tone_Head_Rest_Cover")
      .replaceAll("DS_", "_")
      .replaceAll("block10", "block9");
  }

  return path_;
}

export default function GLBViewer({
  modelUrls = [],
  dataCateg = "",
  dataOption = "",
  dataSelection = {},
  data = {},
  groupedData = {},
  cameraAnimationTrigger,
  captureCallbackRef,
  setGlbsLoaded,
  sceneRootRef,
  isNight,
  isFoodTrayVisible = true,
}) {
  //
  const seatDetails = getSeatDetails(dataSelection);
  const seatTypeSelection = seatDetails.seatTypeSelection;
  const consoleWidthSelection = seatDetails.consoleWidthSelection;
  const seatCode = useMemo(
    () => getSeatCode(seatTypeSelection, consoleWidthSelection),
    [seatTypeSelection, consoleWidthSelection],
  );

  const handleMaterialClick = (materialName, url) => {
    console.log("Clicked Material:", materialName, " ", url);
  };

  const controlsRef = useRef();
  const dirLightRef = useRef();

  const BACKGROUND_MODE = "off";
  const BACKGROUND_BLUR = 0.8;

  const domeAnglesRef = useRef({ azimuth: 0.0, polar: 0.9 });
  const hdriOffsetRef = useRef(new THREE.Euler(-5.9952, -7.34, 0));

  const [presetId, setPresetId] = useState(() =>
    clampPresetId(ADAPTIVE_QUALITY_CONFIG.startPresetId),
  );

  const [forcedPresetId, setForcedPresetId] = useState(null);
  // RENDERMODE SWITCH
  const [renderEnabled, setRenderEnabled] = useState(true);

  const [manifestUrls, setManifestUrls] = useState([]);

  const [lightFile, setLightFile] = useState({});

  const effectivePresetId = forcedPresetId || presetId;
  const preset = QUALITY_PRESETS[effectivePresetId];

  useEffect(() => {
    setLightFile(getLightsBasedOnSeat(seatCode));
  }, [seatCode]);

  // ADD STITCH VARIANTS
  // Inside GLBViewer component
  const getAllStitchVariants = (originalUrls) => {
    const categoriesToDouble = ["BACK COVER", "BOTTOM COVER", "HEADREST COVER"];
    const newModelUrls = { ...originalUrls };

    Object.keys(newModelUrls).forEach((key) => {
      const shouldDouble = categoriesToDouble.some((cat) => key.includes(cat));

      if (shouldDouble) {
        const originalPaths = newModelUrls[key];

        const nonePaths = originalPaths.map((path) => {
          if (
            path.indexOf("405_MonoTone_BackCover_DS") > -1 ||
            path.indexOf("401_DualTone_BackCover_DS") > -1 ||
            path.indexOf("408_MonoTone_BottomCover_DS") > -1 ||
            path.indexOf("400_DualTone_BottomCover_DS") > -1 ||
            path.indexOf("410_MonoTone_Headrest_DS") > -1 ||
            path.indexOf("411_DualTone_Headrest_DS") > -1
          ) {
            return replaceUrlWithStitchVar(path);
          }
          return path;
        });

        // Combine DS and None paths so React maps over all of them
        newModelUrls[key] = [...originalPaths, ...nonePaths];
      }
    });
    // console.log(newModelUrls);

    return newModelUrls;
  };

  modelUrls = useMemo(() => getAllStitchVariants(modelUrls), [modelUrls]);

  // console.log(dataSelection);

  let washLightSelection =
    dataSelection?.["WASH LIGHTING"]?.["Wash Light Package"]?.[
      "Wash Light Package"
    ];

  let cooktailTableSelection =
    dataSelection?.["BACK EQUIPMENT"]?.["Cocktail Table"]?.["Cocktail Table"];

  let readingLightSelection =
    dataSelection?.["PRIVACY & STOWAGE"]?.["Privacy Divider"]?.[
      "Accent & Reading Light"
    ];
  // console.log(readingLightSelection);

  return (
    <Canvas
      frameloop="demand"
      shadows={renderEnabled}
      dpr={preset.dpr}
      gl={{
        antialias: true,
        powerPreference: "high-performance",
      }}
      onCreated={({ gl }) => {
        gl.setClearColor("#e9e9e9", 1); // <-- YOUR BACKGROUND COLOR
      }}
      eventPrefix="client"
      camera={{ position: [-200, 60, 100], fov: 24, near: 5, far: 300 }}
    >
      <group ref={sceneRootRef}>
        <RenderStack
          isNight={isNight}
          SceneEnvPath={SceneEnvPath}
          controlsRef={controlsRef}
          dirLightRef={dirLightRef}
          presetId={presetId}
          setPresetId={setPresetId}
          forcedPresetId={forcedPresetId}
          setForcedPresetId={setForcedPresetId}
          renderEnabled={renderEnabled}
          setRenderEnabled={setRenderEnabled}
          backgroundMode={BACKGROUND_MODE}
          backgroundBlur={BACKGROUND_BLUR}
          domeAnglesRef={domeAnglesRef}
          hdriOffsetRef={hdriOffsetRef}
        />

        <CaptureHelper
          onCaptureReady={(fn) => {
            if (captureCallbackRef) {
              captureCallbackRef.current = fn;
            } else {
              console.error("captureCallbackRef is null/undefined!");
            }
          }}
        />

        <SharedLoaders>
          {(ktx2Loader) => (
            <Suspense
              fallback={
                <Html
                  style={{
                    marginTop: "-70px",
                    color: "gray",
                    fontSize: "calc(0.72rem + var(--large-screen-font-bump))",
                    pointerEvents: "none",
                    zIndex: "0",
                  }}
                >
                  LOADING..
                </Html>
              }
            >
              {Object.entries(modelUrls).map(([key, paths]) =>
                paths.map((url, index) => {
                  url = getActualUrl(seatCode, url);
                  return (
                    <Model
                      key={`${key}-${index}-${url}`}
                      url={url}
                      groupUrlKey={key}
                      groupUrl={modelUrls[key]}
                      dataCateg={dataCateg}
                      dataSelection={dataSelection}
                      seatCode={seatCode}
                      dataOption={dataOption}
                      data={data}
                      groupedData={groupedData}
                      setGlbsLoaded={setGlbsLoaded}
                      onMaterialClick={handleMaterialClick}
                      renderEnabled={renderEnabled}
                      ktx2Loader={ktx2Loader}
                      isFoodTrayVisible={isFoodTrayVisible}
                    />
                  );
                }),
              )}
              {cameraAnimationTrigger}
            </Suspense>
          )}
        </SharedLoaders>

        {/* PRACTICAL_LIGHT */}
        <SeatPracticalLights
          lightFile={lightFile}
          enableBase={washLightSelection === "wash_light_package_yes"}
          enableAccent={readingLightSelection === "collins_lamp_shade"}
          enableCocktail={cooktailTableSelection === "cocktail_table_yes"}
        />

        <DemandOrbitControls
          controlsRef={controlsRef}
          sceneRootRef={sceneRootRef}
        />
      </group>
    </Canvas>
  );
}
